import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  School as TrainingIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../Hooks/useAxiosPrivate";
import styles from "./MyCertifications.module.css";

const dateFormat = (value) => {
  if (!value) return "No expiration";
  // Certifications always expire on December 31 of their year.
  // Pull the year directly to avoid timezone drift that can render 12/31 as 12/30.
  let year;
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})/);
    if (m) year = parseInt(m[1], 10);
  }
  if (!Number.isFinite(year)) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    year = d.getUTCFullYear();
  }
  if (!Number.isFinite(year)) return "";
  const dec31 = new Date(Date.UTC(year, 11, 31));
  return dec31.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

const MyCertifications = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(false);

    axiosPrivate
      .get("/api/my-certifications")
      .then((resp) => {
        if (!cancelled) setAllData(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          enqueueSnackbar("Failed to load certifications", { variant: "error" });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [axiosPrivate, enqueueSnackbar]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allData;
    return allData.filter((row) =>
      String(row?.Certification ?? "").toLowerCase().includes(term) ||
      String(row?.Status ?? "").toLowerCase().includes(term) ||
      String(row?.Notes ?? "").toLowerCase().includes(term)
    );
  }, [searchTerm, allData]);

  const totalCount = filteredData.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const columns = [
    {
      field: "Certification",
      headerName: "Certification",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "Expiration",
      headerName: "Expiration Date",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2">{dateFormat(params.value)}</Typography>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const isActive = params.value === "Active";
        return (
          <Chip
            icon={isActive ? <ActiveIcon /> : <InactiveIcon />}
            label={params.value || "Unknown"}
            color={isActive ? "success" : "error"}
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      field: "Notes",
      headerName: "Notes",
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "TrainingUrl",
      headerName: "Training",
      width: 150,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title="View Training" arrow>
            <a
              href={params.value}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                color: "#003063",
                fontWeight: 600,
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              <TrainingIcon fontSize="small" />
              View
            </a>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
  ];

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography className={styles.loadingText}>Loading certifications...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Error loading certifications. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      {/* Search */}
      <Paper className={styles.searchPaper}>
        <Box className={styles.searchContainer}>
          <TextField
            placeholder="Search by certification, status, or notes"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <ClearIcon
                    className={styles.clearIcon}
                    onClick={() => {
                      setSearchTerm("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  />
                </InputAdornment>
              ) : null,
            }}
            className={styles.searchField}
          />
        </Box>
      </Paper>

      {/* Empty state */}
      {allData.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            You don't have any certifications yet. To become certified, register for{" "}
            <Link
              to="/events?category=Disaster%20Relief%20Training"
              style={{ color: "#003063", fontWeight: 600 }}
            >
              available training classes
            </Link>.
          </Typography>
        </Paper>
      )}

      {/* DataGrid */}
      {allData.length > 0 && (
        <>
          <Paper className={styles.dataGridPaper}>
            <DataGrid
              rows={paginatedData}
              columns={columns}
              getRowId={(row) => row.CertificationID ?? `${row.Certification}-${row.Expiration}`}
              autoHeight
              disableSelectionOnClick
              disableMultipleSelection
              disableColumnFilter
              disableDensitySelector
              hideFooterPagination
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No certifications found</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>You don't have any certifications yet.</Typography>
                  </Box>
                ),
              }}
              className="certDataGrid"
            />
          </Paper>

          {/* Pagination */}
          <Paper className={styles.paginationPaper}>
            <Stack className={styles.paginationStack}>
              <Typography variant="body2" color="text.secondary">
                Showing {totalCount > 0 ? startIndex + 1 : 0} to{" "}
                {Math.min(endIndex, totalCount)} of {totalCount} certification{totalCount !== 1 ? "s" : ""}
              </Typography>

              <Stack className={styles.paginationControls}>
                <Pagination
                  count={pageCount}
                  page={paginationModel.page + 1}
                  onChange={(_, page) =>
                    setPaginationModel((prev) => ({ ...prev, page: page - 1 }))
                  }
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={1}
                  className="certPagination"
                />
              </Stack>

              <Box className={styles.pageSizeSelector}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <TextField
                  select
                  size="small"
                  value={paginationModel.pageSize}
                  onChange={(e) =>
                    setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })
                  }
                  SelectProps={{ native: true }}
                  className={`${styles.pageSizeField} certPageSizeField`}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </TextField>
                <Typography variant="body2" color="text.secondary">per page</Typography>
              </Box>
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MyCertifications;
